import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { CustomersTable } from "../../../components/dashboard/customers/customers-table";
import { CustomersFilter } from "../../../components/dashboard/customers/customers-filter";
import { getCountsAsync } from "../../../slices/inventory/count";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { PlusIcon } from "@heroicons/react/24/outline";
import { toast } from "react-hot-toast";
import { getProjectsAsync } from "../../../slices/project";
import { ProjectsTable } from "../../../components/dashboard/projects/projects-table";
import ProjectDialog from "../../../components/dashboard/projects/dialog";
import { debounce } from "lodash";
import { useSearchParams } from "react-router-dom";
import { updateUrlWithException } from "../../../components/Utils";
import { ModuleFilter } from "../../../components/utils/ModuleFilter";
import { getDepartmentsAsync } from "../../../slices/department";
import { getCustomers } from "../../../slices/customer";
import { getContactsAsync } from "../../../slices/customer/contact";
import { 
  CheckCircleIcon, 
  ClockIcon, 
  ExclamationTriangleIcon,
  DocumentTextIcon 
} from "@heroicons/react/24/outline";

export const ProjectList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const itemState = useSelector((store) => {
    return store.projects;
  });

  const departmentState = useSelector((store) => {
    return store.departments;
  });

  const customerState = useSelector((store) => {
    return store.customers;
  });

  const contactState = useSelector((store) => {
    return store.contacts;
  });

  const { isLoading } = itemState;
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();

  const [controller, setController] = useState({
    // Match ModuleFilter default (inactive_projects off): only active projects from first request.
    filters: [{ name: "active", value: "true" }],
    per_page: 10,
    page: searchParams.get("page") ? searchParams.get("page") : 1,
    query: searchParams.get("query") ? searchParams.get("query") : "",
    sort: searchParams.get("sort") ? searchParams.get("sort") : "desc",
    sortBy: searchParams.get("sortBy")
      ? searchParams.get("sortBy")
      : "created_at",
    view: searchParams.get("view") ? searchParams.get("view") : "all",
    offer_scope: searchParams.get("offer_scope") || "projects",
  });

  // Update all params from controller except filters (dont't touch filters)
  useEffect(() => {
    updateUrlWithException(
      // These will be updated
      {
        page: controller.page,
        query: controller.query,
        sort: controller.sort,
        sortBy: controller.sortBy,
        view: controller.view,
        offer_scope: controller.offer_scope,
        // filters: controller.filters,
      },
      // This will be preserved
      "filters"
    );
  }, [controller]);

  const handleQueryChange = (newQuery) => {
    setController({
      ...controller,
      page: 1,
      query: newQuery,
    });
  };

  const handlePageChange = (newPage) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const handleFiltersChange = (filters) => {
    // Convert ModuleFilter format to backend format
    const backendFilters = [];

    // Handle customer filters
    if (filters.customers && filters.customers.length > 0) {
      filters.customers.forEach((customerId, index) => {
        backendFilters.push({
          name: "customer",
          value: customerId,
        });
      });
    }

    // Handle contact filters
    if (filters.contacts && filters.contacts.length > 0) {
      filters.contacts.forEach((contactId, index) => {
        backendFilters.push({
          name: "contact",
          value: contactId,
        });
      });
    }

    // Handle department filters
    if (filters.departments && filters.departments.length > 0) {
      filters.departments.forEach((departmentId, index) => {
        backendFilters.push({
          name: "department",
          value: departmentId,
        });
      });
    }

    // Handle custom checkbox button filters
    // If inactive_projects is checked, show only inactive projects
    // If inactive_projects is unchecked, show only active projects
    if (filters.inactive_projects) {
      backendFilters.push({
        name: "active",
        value: "false",
      });
    } else {
      backendFilters.push({
        name: "active",
        value: "true",
      });
    }

    setController({
      ...controller,
      page: 1,
      query: filters.query || controller.query,
      filters: backendFilters,
    });
  };

  const [projectDialogOpen, setProjectDialogOpen] = useState(false);

  const debouncedDispatchAction = debounce((controller) => {
    dispatch(getProjectsAsync(controller));
  }, 250);

  useEffect(() => {
    // Trigger the debounced dispatch action
    debouncedDispatchAction(controller);

    // Clean the debounced function on component unmount.
    return () => {
      debouncedDispatchAction.cancel();
    };
  }, [controller, dispatch]);

  // Fetch filter options on component mount
  useEffect(() => {
    // Fetch departments
    dispatch(
      getDepartmentsAsync({
        page: 1,
        per_page: 100,
        sort: "asc",
        sortBy: "title",
      })
    );

    // Fetch customers
    dispatch(
      getCustomers({
        page: 1,
        per_page: 100,
        sort: "asc",
        sortBy: "name",
      })
    );

    // Fetch contacts
    dispatch(
      getContactsAsync({
        page: 1,
        per_page: 100,
        sort: "asc",
        sortBy: "name",
      })
    );
  }, [dispatch]);

  const headerActions = [{}];

  // Convert API data to filter options format
  const customerOptions = customerState.data.map((customer) => ({
    value: customer.id.toString(),
    label: customer.displayTitle || customer.name,
  }));

  const contactOptions = contactState.data.map((contact) => ({
    value: contact.id.toString(),
    label: contact.displayTitle || contact.name,
  }));

  const departmentOptions = departmentState?.data?.map((department) => ({
    value: department.id.toString(),
    label: department.title,
  }));

  // Custom checkbox button filters for projects
  const customCheckboxButtonFilters = [
    {
      key: "inactive_projects",
      label: "Inactive Projects",
      icon: ExclamationTriangleIcon,
      defaultValue: false,
    }
  ];

  // Convert backend filters to ModuleFilter format
  const getInitialFilters = () => {
    const initialFilters = {
      query: controller.query,
      customers: [],
      contacts: [],
      departments: [],
      // Initialize custom checkbox button filters
      inactive_projects: false, // Default to showing active projects only
    };

    if (controller.filters && controller.filters.length > 0) {
      controller.filters.forEach((filter) => {
        switch (filter.name) {
          case "customer":
            initialFilters.customers.push(filter.value);
            break;
          case "contact":
            initialFilters.contacts.push(filter.value);
            break;
          case "department":
            initialFilters.departments.push(filter.value);
            break;
          case "active":
            initialFilters.inactive_projects = filter.value === "false";
            break;
        }
      });
    }

    return initialFilters;
  };

  return (
    <>
      <DashboardHeader
        subtitle=""
        actions={headerActions}
        title={"Projects"}
        meta={[]}
        mainButton={{
          label: "New",
          action: () => setProjectDialogOpen(true),
          path: null,
          color: "",
          icon: PlusIcon,
        }}
      >
        <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
            <ModuleFilter
              className={"mt-10"}
              disabled={isLoading}
              onFiltersChange={handleFiltersChange}
              initialFilters={getInitialFilters()}
              searchPlaceholder="Search projects..."
              filterTitle="Filter Projects"
              customerOptions={customerOptions}
              contactOptions={contactOptions}
              departmentOptions={departmentOptions}
              customCheckboxButtonFilters={customCheckboxButtonFilters}
              segmentedControl={{
                value: controller.offer_scope || "projects",
                onChange: (v) =>
                  setController((c) => ({
                    ...c,
                    page: 1,
                    offer_scope: v,
                  })),
                options: [
                  { value: "projects", label: t("project_list.scope.projects") },
                  { value: "offers", label: t("project_list.scope.offers") },
                  { value: "all", label: t("project_list.scope.all") },
                ],
              }}
            />

            <ProjectsTable
              itemState={itemState}
              itemsCount={itemState?.totalCount}
              currentCount={itemState?.data?.length}
              page={controller.page}
              perPage={controller.per_page}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </DashboardHeader>

      <ProjectDialog
        open={projectDialogOpen}
        onClose={() => setProjectDialogOpen(false)}
        readOnly={false}
        // selected={{ report.customer }}
        // Only for testing
        // selected={item?.customer}
        // onRefresh={() => getCase()}
      />
    </>
  );
};
